/**
 * PAGE DE VENTE PUBLIQUE — Académie / Formations en ligne (accès non-membres).
 * Route publique /academie : hero, disciplines, contenu inclus, tarifs, inscription (checkout Stripe).
 */
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import PublicContactSection from "../components/PublicContactSection";
import {
  GraduationCap, CheckCircle, CircleNotch, ArrowRight, PlayCircle, Barbell,
  Path, Medal, Trophy, X, Timer,
} from "@phosphor-icons/react";

const HERO = "https://images.unsplash.com/photo-1570877920464-4c2d38501303?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const INCLUDED = [
  { icon: Barbell, t: "Techniques par ceinture", d: "Toute la matière technique organisée par ceinture, de la Jaune à la Noire." },
  { icon: Path, t: "Combinaisons & enchaînements", d: "Séquences propres à chaque discipline." },
  { icon: Timer, t: "Programmes 20/30/45 min", d: "Séances prêtes à suivre, ceinture par ceinture." },
  { icon: Medal, t: "Carnet de progression", d: "Suis ton évolution et ta prochaine ceinture." },
  { icon: PlayCircle, t: "Vidéos exclusives", d: "Contenu vidéo réservé aux abonnés (à venir)." },
  { icon: Trophy, t: "Préparation compétition", d: "Programmes de compétition dédiés." },
];

export default function AcademiePublic() {
  const [products, setProducts] = useState([]);
  const [disciplines, setDisciplines] = useState([]);
  const [trialDays, setTrialDays] = useState(0);
  const [promoBanner, setPromoBanner] = useState(null);
  const [signup, setSignup] = useState(null); // product

  useEffect(() => {
    api.get("/academy/products")
      .then((r) => {
        setProducts(r.data.products || []);
        setDisciplines(r.data.disciplines || []);
        const g = r.data.general || {};
        setTrialDays(g.trial_days_effective ?? g.trial_days ?? 0);
        const pb = g.promo_banner;
        // Promo unique = « 19,99 $ / 100 premiers » (géré par launch-status). On masque
        // l'ancienne bannière code (ex. PREMIERE) pour éviter deux offres contradictoires.
        if (pb && pb.enabled && pb.code && pb.force_show) setPromoBanner(pb);
      })
      .catch(() => {});
    const p = new URLSearchParams(window.location.search);
    if (p.get("academy") === "cancel") toast.info("Paiement annulé — tu peux réessayer quand tu veux.");
  }, []);

  const bundle = products.find((p) => p.kind === "bundle");
  const discPlans = products.filter((p) => p.kind === "discipline");

  return (
    <div className="min-h-screen bg-[#0A0908] text-white" data-testid="academie-public">
      {/* BANNIÈRE PROMO */}
      {promoBanner && (
        <div className="bg-[#D90429] text-white text-center px-4 py-2.5 text-sm font-medium flex items-center justify-center gap-2 flex-wrap" data-testid="promo-banner">
          <span>🎟️ {promoBanner.text || "Offre spéciale"}</span>
          <span className="inline-flex items-center bg-white text-[#D90429] font-bold tracking-widest px-2.5 py-0.5 kz-mono" data-testid="promo-banner-code">
            {promoBanner.code}
          </span>
        </div>
      )}

      {/* HERO */}
      <section className="relative min-h-[70vh] flex items-center">
        <div className="absolute inset-0">
          <img src={HERO} alt="Entraînement arts martiaux" className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0908] via-[#0A0908]/85 to-[#0A0908]/40" />
        </div>
        <div className="relative max-w-6xl mx-auto px-6 py-20 w-full">
          <div className="flex items-center gap-2 mb-4">
            <GraduationCap size={20} weight="fill" className="text-[#C9A961]" />
            <span className="kz-mono text-[11px] tracking-[0.3em] uppercase text-[#C9A961]">Académie · Formations en ligne</span>
          </div>
          <h1 className="kz-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-3xl">
            Du dojo à la scène<br /><span className="text-[#D90429]">internationale.</span>
          </h1>
          <p className="kz-body text-base md:text-lg text-white/70 mt-5 max-w-xl">
            Une même structure pour élever ton art. Technique, progression et entraînement réunis dans une seule expérience — Kickboxing / Muay Thai, Jiu-jitsu Can Ryu et BJJ.
          </p>
          <SummerCountdown />
          <div className="flex flex-wrap gap-3 mt-8">
            <a href="#tarifs" data-testid="hero-cta"
              className="inline-flex items-center gap-2 bg-[#D90429] text-white font-semibold px-7 py-3.5 hover:opacity-90 transition-opacity">
              Voir les abonnements <ArrowRight size={17} />
            </a>
            <a href="/membres" className="inline-flex items-center gap-2 border border-white/20 text-white px-7 py-3.5 hover:bg-white/[0.05] transition-colors">
              J'ai déjà un accès
            </a>
          </div>
        </div>
      </section>

      {/* DISCIPLINES */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <h2 className="kz-display text-2xl md:text-3xl mb-8">Trois disciplines, une seule académie</h2>
        <div className="grid md:grid-cols-3 gap-5">
          {disciplines.map((d) => (
            <div key={d.code} className="border border-white/10 bg-white/[0.02] p-6" data-testid={`public-disc-${d.code}`}>
              <div className="w-3 h-3 rounded-full mb-4" style={{ background: d.accent }} />
              <h3 className="kz-display text-xl">{d.name}</h3>
              <p className="kz-body text-sm text-white/55 mt-2">{d.tagline}</p>
            </div>
          ))}
        </div>
      </section>

      {/* INCLUS */}
      <section className="bg-white/[0.02] border-y border-white/8">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <h2 className="kz-display text-2xl md:text-3xl mb-8">Ce que tu obtiens</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {INCLUDED.map((f, i) => (
              <div key={i} className="flex gap-3">
                <f.icon size={22} weight="fill" className="text-[#C9A961] shrink-0 mt-0.5" />
                <div>
                  <div className="text-white font-medium text-sm">{f.t}</div>
                  <div className="text-white/50 text-xs mt-0.5">{f.d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TARIFS */}
      <section id="tarifs" className="max-w-6xl mx-auto px-6 py-16">
        <h2 className="kz-display text-2xl md:text-3xl mb-2">Choisis ton accès</h2>
        <p className="text-white/50 text-sm mb-10">
          {trialDays > 0 ? `${trialDays} jours d'essai inclus · ` : ""}Sans engagement — annule quand tu veux. Accès immédiat.
          {promoBanner && (
            <span className="block mt-1 text-[#C9A961]">
              💡 Entre le code <b className="text-white">{promoBanner.code}</b> à l'étape de paiement pour ta réduction.
            </span>
          )}
        </p>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {discPlans.map((p) => (
            <PlanCard key={p.code} p={p} trialDays={trialDays} onPick={() => setSignup(p)} />
          ))}
          {bundle && <PlanCard p={bundle} trialDays={trialDays} featured onPick={() => setSignup(bundle)} />}
        </div>
      </section>

      {/* FOOTER */}
      <PublicContactSection source="academie" />

      <footer className="border-t border-white/8 py-10 text-center">
        <p className="kz-mono text-[11px] tracking-[0.25em] uppercase text-[#C9A961]">Muay Thai sans compromis</p>
        <p className="text-white/40 text-xs mt-2">Institut Impact Élite Martial · Val-d'Or · (819) 527-9446</p>
      </footer>

      {signup && <SignupModal product={signup} trialDays={trialDays} onClose={() => setSignup(null)} />}
    </div>
  );
}

function SummerCountdown() {
  const [st, setSt] = useState(null);
  useEffect(() => {
    api.get("/kaizen-online/launch-status").then((r) => setSt(r.data)).catch(() => {});
  }, []);
  if (!st || !st.promo_active) return null;
  const pctTaken = st.spots_total ? Math.min(100, Math.round((st.spots_taken / st.spots_total) * 100)) : 0;
  return (
    <div className="mt-6 border border-[#C9A961]/40 bg-[#C9A961]/[0.06] px-5 py-4 max-w-md" data-testid="launch-promo">
      <div className="flex items-center gap-4">
        <div className="text-left flex-1">
          <div className="kz-mono text-[10px] tracking-[0.2em] uppercase text-[#C9A961]">🎯 Offre 100 premiers abonnés</div>
          <div className="text-white/70 text-xs">
            <b className="text-white">{st.launch_price?.toFixed(2)} $</b>/mois pendant 1&nbsp;an
            <span className="text-white/40"> (au lieu de {st.regular_price?.toFixed(2)} $)</span>
          </div>
          <div className="text-white/35 text-[10px] mt-0.5">puis {st.regular_price?.toFixed(2)} $/mois · annulable en tout temps</div>
        </div>
        <div className="flex flex-col items-center border-l border-white/15 pl-4">
          <span className="kz-display text-2xl md:text-3xl text-[#C9A961] tabular-nums" data-testid="launch-spots-left">{st.spots_left}</span>
          <span className="kz-mono text-[9px] tracking-widest uppercase text-white/50">places restantes</span>
        </div>
      </div>
      <div className="mt-3 h-2 rounded-full bg-white/10 overflow-hidden" data-testid="launch-progress">
        <div className="h-full rounded-full bg-gradient-to-r from-[#C9A961] to-[#D90429] transition-all duration-700" style={{ width: `${pctTaken}%` }} />
      </div>
      <div className="mt-1 flex justify-between text-[9px] kz-mono uppercase tracking-widest text-white/40">
        <span>{st.spots_taken} / {st.spots_total} pris</span>
        <span className="text-[#C9A961]">Dépêche-toi 🔥</span>
      </div>
    </div>
  );
}

function PlanCard({ p, featured, trialDays, onPick }) {
  return (
    <div className={`relative border p-6 flex flex-col ${featured ? "border-[#C9A961] bg-[#C9A961]/[0.06]" : "border-white/10 bg-white/[0.02]"}`}
      data-testid={`plan-${p.code}`}>
      {featured && (
        <span className="absolute -top-3 left-6 bg-[#C9A961] text-black text-[10px] font-bold px-3 py-1 kz-mono tracking-wider">POPULAIRE</span>
      )}
      <h3 className="kz-display text-lg leading-tight">{p.name}</h3>
      <div className="mt-4 mb-1">
        <span className="kz-display text-3xl">{p.price_cad.toFixed(2)}&nbsp;$</span>
        <span className="text-white/40 text-sm"> / mois</span>
      </div>
      {trialDays > 0 && (
        <span className="inline-block w-fit bg-green-500/15 text-green-400 text-[11px] font-semibold px-2 py-0.5 mb-2" data-testid={`plan-trial-${p.code}`}>
          🎁 {trialDays} jours d'essai inclus
        </span>
      )}
      <p className="text-white/45 text-xs mb-5">
        {p.kind === "bundle" ? "Accès aux 3 disciplines" : "Accès à 1 discipline"}
      </p>
      <button onClick={onPick} data-testid={`plan-subscribe-${p.code}`}
        className={`mt-auto inline-flex items-center justify-center gap-2 py-3 text-sm font-semibold transition-opacity hover:opacity-90 ${featured ? "bg-[#C9A961] text-black" : "bg-[#D90429] text-white"}`}>
        {trialDays > 0 ? "Commencer ma période d'essai" : "S'abonner"} <ArrowRight size={15} />
      </button>
    </div>
  );
}

function SignupModal({ product, trialDays, onClose }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  async function subscribe() {
    if (!email.trim() || !name.trim()) { toast.error("Nom et courriel requis."); return; }
    setBusy(true);
    try {
      const r = await api.post("/academy/checkout", {
        email: email.trim(), full_name: name.trim(),
        product_code: product.code, origin_url: window.location.origin,
      });
      if (r.data.checkout_url) window.location.href = r.data.checkout_url;
      else throw new Error();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de démarrer le paiement.");
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[120] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="signup-modal" onClick={onClose}>
      <div className="w-full max-w-md bg-[#0C0A09] border border-white/15 p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="kz-display text-xl">S'abonner</h3>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X size={20} /></button>
        </div>
        <p className="text-white/50 text-sm mb-5">{product.name} · {product.price_cad.toFixed(2)} $/mois{trialDays > 0 ? ` · ${trialDays} jours d'essai inclus` : ""}</p>
        <label className="block text-white/50 text-xs mb-1">Ton nom complet</label>
        <input value={name} onChange={(e) => setName(e.target.value)} data-testid="signup-name"
          className="w-full bg-black/40 border border-white/15 text-white text-sm px-3 py-2.5 mb-3" placeholder="Prénom Nom" />
        <label className="block text-white/50 text-xs mb-1">Ton courriel</label>
        <input value={email} onChange={(e) => setEmail(e.target.value)} data-testid="signup-email" type="email"
          className="w-full bg-black/40 border border-white/15 text-white text-sm px-3 py-2.5 mb-4" placeholder="toi@courriel.com" />
        <button onClick={subscribe} disabled={busy} data-testid="signup-submit"
          className="w-full bg-[#D90429] text-white font-semibold py-3 hover:opacity-90 disabled:opacity-50 inline-flex items-center justify-center gap-2">
          {busy ? <CircleNotch size={16} className="animate-spin" /> : <>Continuer vers le paiement <ArrowRight size={15} /></>}
        </button>
        <p className="text-white/35 text-[11px] mt-3 flex items-center gap-1">
          <CheckCircle size={13} className="text-green-400" /> Paiement sécurisé par Stripe · annule quand tu veux
        </p>
      </div>
    </div>
  );
}
